import React from 'react';
import {observer} from 'mobx-react';
import {withState, t} from 'shared/core';
import {MassUpdateEditorState} from '../state';
import {
  AutosaveStatus,
  DatePicker,
  FormError,
  FormLayout,
  Button,
  TextArea,
  Display,
  Layout,
  Panel,
  Tag,
  CheckboxWithLabel
} from 'components';
import {FormattedMessage} from 'react-intl';
import MassUpdateEmployeeEditTable from './MassUpdateEmployeeEditTable';
import AddEmployeesModal from './AddEmployeesModal';
import PublishConfirmationModal from './PublishConfirmationModal';
import FIELDS from 'containers/mass_updates/fields';
import _ from 'lodash';
import BulkEditModal from 'containers/mass_updates/edit/components/BulkEditModal';

const PublishButton = observer(({uiState}) => {
  const {massUpdate} = uiState;

  if (!massUpdate.hasLink('publish')) {
    return null;
  }

  return (
    <div className='right'>
      <AutosaveStatus autosaver={massUpdate.autosaver}/>
      <Button className='pl2' onClick={() => uiState.openPublishConfirmationModal()}>
        <FormattedMessage id='mass_updates.edit.Publish'/>
      </Button>
    </div>
  );
});

const MassUpdateHeader = observer(({massUpdate}) => {
  return (
    <div className='flex align-items-middle'>
      <FormattedMessage id='mass_updates.edit.Mass Edit Employees'/>
      {massUpdate.running && <Tag className='h4 ml1 align-text-top' colour='meadow'>
        <FormattedMessage id='mass_updates.edit.Running'/>
      </Tag>}
    </div>
  );
});

const MassUpdateEditor = observer(({uiState}) => {
  const {massUpdate, errors} = uiState;

  return (
    <div>
      <Display
        title={<MassUpdateHeader massUpdate={massUpdate}/>}
        backPath='/'
      />
      <Layout>
        <Panel.Stack loose>
          <Panel>
            <Panel.Header title={t('mass_updates.edit.Effective Date')}/>
            <FormLayout>
              <DatePicker
                value={massUpdate.effectiveDate}
                onChange={(effectiveDate) => uiState.updateMassUpdate({effectiveDate})}
                options={{startView: 1}}
                className='full-width'
                disabled={massUpdate.readOnly}
                errorMessage={errors.effectiveDate}
              />
            </FormLayout>
            <Panel.Header
              title={t('mass_updates.edit.Additional Notes')}
              description={t('mass_updates.edit.additional_notes_desc')}
            />
            <TextArea
              label='mass_updates.edit.Comment (not visible to the employee)'
              value={massUpdate.comment}
              onChange={(e) => uiState.updateMassUpdate({comment: e.target.value})}
              placeholder='mass_updates.edit.Enter your comment here'
              className='full-width mb2' minRows={3}
              disabled={massUpdate.readOnly}
            />
            <React.Fragment>
              <Panel.Header
                title={t('mass_updates.edit.Attributes')}
                description={t('mass_updates.edit.attributes_explainer')}
              />
              <div className='flex flex-column space-y'>
                {FIELDS.map(field => (
                  <CheckboxWithLabel
                    label={`mass_updates.edit.fields.${field}`}
                    checked={_.includes(massUpdate.includedFields, field)}
                    disabled={massUpdate.readOnly}
                    onChange={e => uiState.updateIncludedField(field, e.checked)}
                  />
                ))}
              </div>
              <FormError message={errors.includedFields}/>
            </React.Fragment>
          </Panel>
          <MassUpdateEmployeeEditTable uiState={uiState}/>
          <FormError message={errors.base} alignRight/>
          <PublishButton uiState={uiState}/>
        </Panel.Stack>
      </Layout>
      <AddEmployeesModal uiState={uiState}/>
      <PublishConfirmationModal uiState={uiState}/>
      <BulkEditModal uiState={uiState}/>
    </div>
  );
});

export default withState(MassUpdateEditor, MassUpdateEditorState);
